import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { TopMaintenanceCostDashboardDto } from 'api/dashboard/dtos/TopMaintenanceCostDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import { getPreviousRange, PreviousRange } from '../utils/getPreviousRange';
import openMaintenancesList from '../utils/openMaintenancesList';
import styles from './TopMaintenanceCosts.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

type Props = {
	// Legacy props (direct data passing)
	data?: TopMaintenanceCostDashboardDto[] | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const TopMaintenanceCosts = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<TopMaintenanceCostDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [previousTotal, setPreviousTotal] = useState<number | null>(null);
	const [previousRange, setPreviousRange] = useState<PreviousRange | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topMaintenanceCosts');

	const isLegacyMode = props.data !== undefined;

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			setPreviousTotal(null);
			setPreviousRange(null);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getTopMaintenanceCosts(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}

			try {
				const range = getPreviousRange(dateFrom, dateTo);
				const prevCriteria = props.filters
					? { ...props.filters, dateFrom: range.prevFrom, dateTo: range.prevTo }
					: { dateFrom: range.prevFrom, dateTo: range.prevTo };
				const previous = await DashboardService.getTopMaintenanceCosts(prevCriteria);
				if (!cancelled) {
					setPreviousTotal((previous || []).reduce((s, x) => s + x.totalCost, 0));
					setPreviousRange(range);
				}
			} catch {
				if (!cancelled) {
					setPreviousTotal(null);
					setPreviousRange(null);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const items = data || [];
	const maxCost = Math.max(...items.map(d => d.totalCost), 0);
	const grandTotal = items.reduce((sum, it) => sum + it.totalCost, 0);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: TopMaintenanceCostDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.top_maintenance_costs') as string}
			subTitleTooltip={t('dashboard.top_maintenance_costs_tooltip' as any) as string}
			renderOptions={!isLegacyMode ? <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} /> : undefined}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={Utils.formatCurrency(grandTotal)}
						subLabel={t('dashboard.total_cost' as any) as string}
						currentTotal={grandTotal}
						previousTotal={previousTotal}
						previousRange={previousRange}
						onClick={() => openMaintenancesList({ dateFrom, dateTo })}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const barWidth = maxCost > 0 ? (item.totalCost / maxCost) * 100 : 0;
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div
								key={item.vehicleId}
								className={styles.row}
								onClick={() => openMaintenancesList({
									dateFrom,
									dateTo,
									vehicle: { id: item.vehicleId, name: item.registrationNumber },
								})}
							>
								<span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="top-maintenance-costs-tooltip" data-place="right">{item.registrationNumber}</span></span>
								<div className={styles.barTrack}>
									<div
										className={styles.barFill}
										style={{ width: `${barWidth}%`, background: color }}
									/>
								</div>
								<span className={styles.cost}>
									{Utils.formatCurrency(item.totalCost)}
								</span>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="top-maintenance-costs-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default TopMaintenanceCosts;
